const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

/**
 * Cached async fetch with stale time.
 * - Cache hit within stale time → returns instantly
 * - Cache miss or stale → fetches, stores, returns
 * - Deduplicates concurrent requests to the same key
 */
export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  staleTime = DEFAULT_STALE_TIME
): Promise<T> {
  const entry = cache.get(key) as CacheEntry<T> | undefined;

  if (entry && Date.now() - entry.timestamp < staleTime) {
    return entry.data;
  }

  // Deduplicate concurrent calls
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, timestamp: Date.now() });
      return data;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

export function invalidateCache(key: string): void {
  cache.delete(key);
}
